import { useEffect, useRef, useState } from 'react';
import './IndustrialHeader.css';

const destinations = [
  ['cannedjobs', 'Canned Jobs', '🛠️', '#E7B888'],
  ['customers', 'Customers', '👥', '#B78F7A'],
  ['dashboard', 'Dashboard', '📊', '#2D5F3F'],
  ['estimates', 'Estimates', '📝', '#6B8E23'],
  ['expenses', 'Expenses', '🧾', '#E6BCBC'],
  ['inventory', 'Inventory', '📦', '#FFF200'],
  ['invoices', 'Invoices', '💰', '#B7410E'],
  ['mileage', 'Mileage', '🚗', '#60A5FA'],
  ['calendar', 'Scheduler', '📅', '#365C91'],
  ['settings', 'Settings', '⚙️', '#E8761D'],
  ['taxcenter', 'Tax Center', '🏛️', '#004d40']
];

const IndustrialHeader = ({ navigate, onLogout }) => {
  const [isOpen, setIsOpen] = useState(false);
  const headerRef = useRef(null);

  useEffect(() => {
    const closeOnOutsideClick = (event) => {
      if (headerRef.current && !headerRef.current.contains(event.target)) setIsOpen(false);
    };
    const closeOnEscape = (event) => {
      if (event.key === 'Escape') setIsOpen(false);
    };

    document.addEventListener('mousedown', closeOnOutsideClick);
    document.addEventListener('keydown', closeOnEscape);
    return () => {
      document.removeEventListener('mousedown', closeOnOutsideClick);
      document.removeEventListener('keydown', closeOnEscape);
    };
  }, []);

  useEffect(() => {
    if (!isOpen || window.innerWidth > 1023) return undefined;

    const previousBodyOverflow = document.body.style.overflow;
    const previousBodyOverscroll = document.body.style.overscrollBehavior;
    const previousRootOverflow = document.documentElement.style.overflow;
    document.body.style.overflow = 'hidden';
    document.body.style.overscrollBehavior = 'none';
    document.documentElement.style.overflow = 'hidden';

    return () => {
      document.body.style.overflow = previousBodyOverflow;
      document.body.style.overscrollBehavior = previousBodyOverscroll;
      document.documentElement.style.overflow = previousRootOverflow;
    };
  }, [isOpen]);

  const goHome = () => {
    setIsOpen(false);
    navigate('dashboard');
  };

  const goToDestination = (id) => {
    setIsOpen(false);
    navigate(id);
  };

  return (
    <header
      className="mtn-industrial-header"
      ref={headerRef}
      aria-label="MyTool NOW navigation"
      style={{
        '--mtn-diamond-plate': "url('/MTN-RUSTY-DIAMOND-PLATE.png')",
        '--mtn-cast-iron': "url('/MTN-WEATHERED-CAST-IRON.png')"
      }}
    >
      <div className="mtn-header-brand">
        <img src="/mtnlogo.png" alt="MyTool NOW" />
      </div>

      <div className="mtn-header-home-zone">
        <div className="mtn-metal-frame mtn-home-frame">
          <button className="mtn-home-button" type="button" onClick={goHome} aria-label="Home">
            <svg aria-hidden="true" viewBox="0 0 24 24" focusable="false">
              <defs>
                <linearGradient id="mtn-home-metal" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#fff0a4" />
                  <stop offset="12%" stopColor="#ffd14a" />
                  <stop offset="38%" stopColor="#ff9a00" />
                  <stop offset="72%" stopColor="#d85000" />
                  <stop offset="100%" stopColor="#7b2100" />
                </linearGradient>
              </defs>
              <path className="mtn-home-metal-icon" fill="url(#mtn-home-metal)" d="M12 2.4 2.4 10v11.1c0 .8.7 1.5 1.5 1.5h5.7v-7h4.8v7h5.7c.8 0 1.5-.7 1.5-1.5V10L12 2.4Z" />
            </svg>
            <span className="mtn-home-label">Home</span>
          </button>
        </div>
      </div>

      <div className="mtn-header-nav-zone">
        <div className="mtn-metal-frame mtn-next-frame">
          <button
            className="mtn-next-button"
            type="button"
            aria-expanded={isOpen}
            aria-controls="mtn-destination-menu"
            onClick={() => setIsOpen((open) => !open)}
          >
            <span className="mtn-next-label">Where to next?</span><span className="mtn-next-arrow" aria-hidden="true">▼</span>
          </button>
        </div>
        <section
          className={`mtn-destination-menu${isOpen ? ' is-open' : ''}`}
          id="mtn-destination-menu"
          aria-label="Module destinations"
        >
          <button className="mtn-menu-close" type="button" onClick={() => setIsOpen(false)} aria-label="Close module menu">Close menu</button>
          <p className="mtn-menu-label">Select a MyTool NOW module</p>
          <div className="mtn-destination-grid">
            {destinations.map(([id, label, icon, color]) => (
              <button
                className="mtn-destination"
                key={id}
                type="button"
                style={{ '--mtn-module-color': color }}
                onClick={() => goToDestination(id)}
              >
                <span className="mtn-destination-icon" aria-hidden="true">{icon}</span>
                <span>{label}</span>
              </button>
            ))}
          </div>
        </section>
      </div>

      <div className="mtn-header-mascot">
        <img src="/MTN-HEADER-ROBOT.png" alt="MyTool NOW thumbs-up robot mascot" />
      </div>

      <div className="mtn-header-flag-zone">
        <div className="mtn-metal-frame mtn-flag-frame">
          <img src="/usflag.png" alt="United States flag" />
        </div>
      </div>

      <div className="mtn-logout-frame">
        <button className="mtn-logout-button" type="button" onClick={onLogout} aria-label="Log out of MyTool NOW">
          <span className="mtn-logout-power" aria-hidden="true">⏻</span>
          <span className="mtn-logout-label">QUITTIN' TIME!</span>
        </button>
      </div>
    </header>
  );
};

export default IndustrialHeader;
